import { Formik, Form } from 'formik'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Button } from '@/components/button'
import { FieldInput, FieldTextarea } from '@/components/form'
import useMediaQuery from '@/hooks/use-media-query'
import { ParagraphSmall } from '@/components/text'
import { breakpoints, fontWeights, colors } from '@/styles'
import { useAddendumModal } from '../context'

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 540px;
`

const Notice = styled.div`
  background-color: ${colors.white};
  border-radius: 6px;
  padding: 16px;
  margin: 0 0 30px;
`

const Footnote = styled.div`
  font-weight: ${fontWeights.medium};
  margin: 30px 0 40px;
  > div {
    font-weight: 600;
  }
`

const StyledButton = styled(Button)`
  display: block;
  margin-left: auto;
`

const Edit = ({ setEditing, addendumState, setAddendumState }) => {
  const { t } = useTranslation()
  const {
    ModalFooter,
    close,
    backStep,
    isAdmin,
    addendumType,
    footnote,
    buttonText,
    onSubmit,
    onSubmitComplete
  } = useAddendumModal()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)

  const submitCompleteAction = () => {
    if (onSubmitComplete === 'close') return close()
    if (onSubmitComplete === 'back') return backStep()
    if (onSubmitComplete === 'view') return setEditing(false)
    return
  }

  const initialValues = {
    title: addendumState?.title || '',
    content: addendumState?.content || ''
  }

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(),
    content: Yup.string().required()
  })

  const handleOnSubmit = async (values, { setSubmitting }) => {
    const formattedAddendum = {
      title: values.title,
      content: values.content,
      data_type: 'content',
      addendum_type: addendumType,
      metadata: null,
      listing_contract_addendum_template_id:
        addendumState?.listing_contract_addendum_template_id || null
    }
    setAddendumState(formattedAddendum)
    onSubmit(formattedAddendum)
    setSubmitting(false)
    submitCompleteAction()
  }

  return (
    <ContentInner>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ values, setFieldValue, isSubmitting, handleSubmit }) => {
          return (
            <Form>
              <Notice>
                <ParagraphSmall>
                  {t(
                    'l.contract.edit.edit_addendum_legal_notice.text',
                    'Any additional addendums to this tenancy agreement must comply with the Residential Tenancy Act and regulations. The term is not enforceable if the term does not meet those requirements.'
                  )}
                </ParagraphSmall>
              </Notice>
              <FieldInput
                name="title"
                value={values?.title}
                onChange={e => setFieldValue('title', e.target.value)}
                placeholder={t(
                  'l.contract.edit.custom_addendum_modal_title_field.placeholder',
                  'Title'
                )}
              />
              <FieldTextarea
                name="content"
                value={values?.content}
                height={isMobile ? 300 : 140}
                top={30}
                onChange={e => setFieldValue('content', e.target.value)}
                placeholder={t(
                  'l.contract.edit.custom_addendum_modal_content.placeholder',
                  'Describe your custom addendum'
                )}
              />
              <Footnote>
                <ParagraphSmall>{isAdmin ? footnote : ''}</ParagraphSmall>
              </Footnote>
              <ModalFooter>
                <StyledButton theme="primary" onClick={handleSubmit} disabled={isSubmitting}>
                  {buttonText}
                </StyledButton>
              </ModalFooter>
            </Form>
          )
        }}
      </Formik>
    </ContentInner>
  )
}

export default Edit
